import Phase from 'app/models/phase';
import { GroupableByPhase } from 'app/shared/components/phase-collections';
import { Subscribable } from 'app/models/subscribable';
import InstanceSubscription from 'app/models/instance-subscription';
import TimelineFieldCalendarEvent from 'app/models/timeline-field-calendar-event';
import { isNil } from 'lodash-es';
import { Timelineable } from './timeline';

export const EXPECTED_PURCHASE_PRICE_TIMELINE_FIELD_SLUG = 'expected-purchase-price-amount-in-cents';
export const FINAL_PURCHASE_PRICE_TIMELINE_FIELD_SLUG = 'final-purchase-price-amount-in-cents';
export const EXPECTED_REVENUE_GROUP = 'expected-revenue';
export const FINAL_REVENUE_GROUP = 'final-revenue';

export const REVENUE_CALCULATION_TIMELINE_FIELDS = [
  'expected-gross-commission-amount-in-cents',
  'expected-rebate-amount-in-cents',
  'expected-referral-fee-amount-in-cents',
  'expected-additional-fees-amount-in-cents',
  'expected-additional-concessions-amount-in-cents',
  'expected-net-commission-amount-in-cents',

  'final-gross-commission-amount-in-cents',
  'final-rebate-amount-in-cents',
  'final-referral-fee-amount-in-cents',
  'final-additional-fees-amount-in-cents',
  'final-additional-concessions-amount-in-cents',
  'final-net-commission-amount-in-cents',
];

export interface TimelineFieldable extends GroupableByPhase {
  id?: number;
  name?: string;
  slug: string;
  value?: any;
  value_type?: string;
  group_phase?: Phase;
  required?: boolean;
  subscribable_slug?: string;
  subscribable_type?: string;
  subscribable_name?: string;
  subscribable_attribute?: string;
  subscribable_value?: string;
  instance_subscriptions?: InstanceSubscription[];
  timeline_field_calendar_event?: TimelineFieldCalendarEvent;
  group_slug?: string;
  sequence_number?: number;
  calendar_synced?: boolean;
  client_viewable?: boolean;
  mls_locked?: boolean;
  qualia_locked?: boolean;
  step_timeline_fields?: {
    client_viewable: boolean;
    required: boolean;
    step_id: number;
  }[]
}

export default class TimelineField implements Subscribable, GroupableByPhase, TimelineFieldable {
  id?: number;
  name: string;
  slug: string;
  value?: any;
  value_type: string;
  group_phase: Phase;
  required: boolean;
  subscribable_slug: string;
  subscribable_type: string;
  subscribable_name: string;
  subscribable_attribute: string;
  subscribable_value: string;
  instance_subscriptions: InstanceSubscription[];
  timeline_field_calendar_event?: TimelineFieldCalendarEvent;
  group_slug: string;
  sequence_number: number;
  calendar_synced?: boolean;
  client_viewable: boolean;
  mls_locked: boolean;
  qualia_locked: boolean;

  constructor(timelineFieldable?: TimelineFieldable) {
    if (timelineFieldable) Object.assign(this, timelineFieldable);
  }
}

export function findTimelineField(timelineFields: TimelineFieldable[], slug: string) {
  return timelineFields.find((timelineField: TimelineFieldable) => timelineField.slug === slug) || null;
}

// takes an array of timeline field slugs as a second value.
// slug array should be in order of preference
export function coalesceTimelineFields(timelineFields: TimelineFieldable[], slugs: string[]) {
  for (const slug of slugs) {
    const timelineField = findTimelineField(timelineFields, slug)
    if (!isNil(timelineField?.value)) return timelineField;
  }

  return findTimelineField(timelineFields, slugs[slugs.length - 1]);
}


export function getContextualPurchasePriceField(timelineFields: TimelineFieldable[], groupSlug: string) {
  const purchasePriceSlug = groupSlug === FINAL_REVENUE_GROUP ? FINAL_PURCHASE_PRICE_TIMELINE_FIELD_SLUG : EXPECTED_PURCHASE_PRICE_TIMELINE_FIELD_SLUG;

  return findTimelineField(timelineFields, purchasePriceSlug);
}
